import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, Lightbulb } from 'lucide-react';
import api from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { C } from '@/design-system/dna';

const HelpCtx = createContext(null);
export const useHelp = () => useContext(HelpCtx);

/** Textos de ajuda (backend, editáveis pela coordenação) + ações globais (central, tutorial). */
export function HelpProvider({ children, actions }) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => api.get('/dna-mapa/ajuda').then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return <HelpCtx.Provider value={{ data, setData, reload, ...actions }}>{children}</HelpCtx.Provider>;
}

const canHover = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

export const HelpBody = ({ h }) => (
  <div className="space-y-2 text-[12.5px] leading-snug" style={{ color: C.ink }}>
    <p><b>Para que serve:</b> {h.para_que}</p>
    <p><b>Como preencher:</b> {h.como}</p>
    {h.exemplo && <p className="flex gap-1.5 rounded-lg bg-[#F5EEFA] p-2"><Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: C.purple }} /><span><b>Exemplo:</b> {h.exemplo}</span></p>}
    {h.erros && <p className="flex gap-1.5 rounded-lg bg-amber-50 p-2 text-amber-900"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span><b>Evite:</b> {h.erros}</span></p>}
  </div>
);

/** Ícone (?) com ajuda do campo: clique/toque, foco por teclado e hover em computadores. */
export function HelpTip({ k, side = 'top' }) {
  const h = useHelp()?.data?.campos?.[k];
  const [open, setOpen] = useState(false);
  const t = useRef();
  if (!h) return null;
  const later = (v, ms) => { clearTimeout(t.current); t.current = setTimeout(() => setOpen(v), ms); };
  const hover = (v) => () => canHover() && later(v, v ? 250 : 180);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`Ajuda: ${h.titulo}`} data-testid={`help-${k}`} onMouseEnter={hover(true)} onMouseLeave={hover(false)}
          onFocus={(e) => e.target.matches(':focus-visible') && setOpen(true)} onClick={(e) => e.stopPropagation()}
          className="ml-1 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full align-middle transition-colors hover:bg-[#F5EEFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B3F98]" style={{ color: C.purple }}>
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align="start" collisionPadding={12} onOpenAutoFocus={(e) => e.preventDefault()} onMouseEnter={hover(true)} onMouseLeave={hover(false)}
        className="w-[min(340px,90vw)] rounded-2xl p-3.5" data-testid={`help-${k}-conteudo`}>
        <p className="mb-2 flex items-center gap-1.5 text-[13px] font-extrabold" style={{ color: C.ink }}><CheckCircle2 className="h-4 w-4" style={{ color: C.purple }} />{h.titulo}</p>
        <HelpBody h={h} />
      </PopoverContent>
    </Popover>
  );
}
